import type { PracticeLocale } from "./practiceLocale";
import type { PracticeSubject } from "./practiceSubjects";
import type { PracticeProblemPart } from "./practiceParts";
import type { PracticeBand } from "./practiceBands";
import {
  type LessonTemplateNotation,
  sym,
  text,
  line,
  ann,
  exp,
  circle,
} from "./lessonTemplates";
import { dataDotPlotFigure } from "./practiceArithmeticFigures";

export type PracticeDifficulty = "intro" | "core" | "challenge";

export const PRACTICE_DIFFICULTY_ORDER: PracticeDifficulty[] = [
  "intro",
  "core",
  "challenge",
];

export const PRACTICE_DIFFICULTY_LABEL: Record<PracticeDifficulty, string> = {
  intro: "Intro",
  core: "Core",
  challenge: "Challenge",
};

export type PracticeTemplatePart = {
  id: string;
  text: string;
  expectedAnswer: string;
  acceptedAnswers?: string[];
  /** Progressive hints for this task only; never state the answer. */
  hints?: string[];
};

export type PracticeQuestionTemplate = {
  /** Stable question board uuid in the database. */
  uuid: string;
  id: string;
  /** Stable skill. Several exercises may share one id. */
  skillId: string;
  /**
   * Inclusive grade span. 0 is kindergarten, 1–12 are grades.
   * Hebrew labels (כיתה א–יב) come from practiceGrades, not from this number.
   */
  gradeMin: number;
  gradeMax: number;
  subject: PracticeSubject;
  name: string;
  /** Canonical answer used for automated practice feedback. */
  expectedAnswer: string;
  /** Optional alternate accepted forms (e.g. factored order). */
  acceptedAnswers?: string[];
  notations: LessonTemplateNotation[];
  /** Authored problem statement. Fallback flattens notations. */
  prompt?: string;
  difficulty?: PracticeDifficulty;
  /** School band (global). Curriculum maps only change the picker labels. */
  band: PracticeBand;
  tags?: string[];
  /** Progressive hints that never state the answer. */
  hints?: string[];
  /** Short method note after a correct check (all parts, if multi-part). */
  explanation?: string;
  /** Explicit multi-part items with per-part answer keys. */
  parts?: PracticeTemplatePart[];
};

const DIAGRAM_STEM_KINDS = new Set([
  "LINE",
  "CURVE",
  "CIRCLE",
  "CONIC",
  "ANNOTATION",
]);

/** Curated practice stems — synced via seeders/seed.bat (with users). */
export const PRACTICE_QUESTION_TEMPLATES: PracticeQuestionTemplate[] = [
  {
    id: "algebra-linear-2x-plus-3",
    skillId: "algebra.linear-one-step",
    gradeMin: 7,
    gradeMax: 8,
    band: "middle",
    uuid: "a1000001-0000-4000-8000-000000000001",
    subject: "Algebra",
    name: "Solve 2x + 3 = 11",
    difficulty: "intro",
    tags: ["linear", "one-step"],
    expectedAnswer: "x = 4",
    acceptedAnswers: ["x=4", "4"],
    prompt: "Solve 2x + 3 = 11\nSolve for x:\n2x+3=11",
    hints: [
      "Undo addition first so the x term is alone.",
      "Then divide both sides by the coefficient of x.",
    ],
    explanation: "Subtract 3 from both sides to get 2x = 8, then divide by 2.",
    notations: [
      text(2, 28, 1, 2, "Solve for x:"),
      sym(2, 5, "2"),
      sym(3, 5, "x"),
      sym(4, 5, "+"),
      sym(5, 5, "3"),
      sym(6, 5, "="),
      sym(7, 5, "1"),
      sym(8, 5, "1"),
    ],
  },
  {
    id: "algebra-factor-quadratic",
    skillId: "algebra.factor-quadratic",
    gradeMin: 10,
    gradeMax: 11,
    band: "high",
    uuid: "a1000001-0000-4000-8000-000000000002",
    subject: "Algebra",
    name: "Factor x² − 5x + 6",
    difficulty: "core",
    tags: ["quadratic", "factoring"],
    expectedAnswer: "(x - 2)(x - 3)",
    acceptedAnswers: ["(x-2)(x-3)", "(x - 3)(x - 2)", "(x-3)(x-2)"],
    prompt: "Factor x² − 5x + 6\nFactor completely:\nx^2-5x+6",
    hints: [
      "Find two numbers that multiply to 6 and add to −5.",
      "Both factors will be of the form (x − n).",
    ],
    explanation:
      "The constant term is 6 and the middle coefficient is −5, so the two numbers multiply to 6 and add to −5. Those numbers are −2 and −3. The factorization is (x − 2)(x − 3). Expanding that product brings back x² − 5x + 6.",
    notations: [
      text(2, 30, 1, 2, "Factor completely:"),
      sym(2, 5, "x"),
      exp(3, 5, "2"),
      sym(4, 5, "-"),
      sym(5, 5, "5"),
      sym(6, 5, "x"),
      sym(7, 5, "+"),
      sym(8, 5, "6"),
    ],
  },
  {
    id: "algebra-system-two-equations",
    skillId: "algebra.system",
    gradeMin: 8,
    gradeMax: 9,
    band: "middle",
    uuid: "a1000001-0000-4000-8000-000000000003",
    subject: "Algebra",
    name: "Solve the system 2x + y = 7, x − y = 2",
    difficulty: "core",
    tags: ["systems", "linear"],
    expectedAnswer: "x = 3, y = 1",
    acceptedAnswers: ["x=3, y=1", "(3, 1)", "(3,1)", "y=1, x=3"],
    prompt:
      "Solve the system 2x + y = 7, x − y = 2\nSolve for x and y:\n2x + y = 7\nx − y = 2",
    hints: [
      "Adding the two equations cancels y.",
      "Once you have x, substitute back into either equation.",
    ],
    explanation: "Add the equations to get 3x = 9, so x = 3. Then y = 7 − 6 = 1.",
    notations: [
      text(2, 36, 1, 2, "Solve the system:"),
      text(2, 28, 4, 5, "2x + y = 7"),
      text(2, 28, 7, 8, "x − y = 2"),
    ],
  },
  {
    id: "algebra-quadratic-vertex-form",
    skillId: "algebra.vertex-form",
    gradeMin: 11,
    gradeMax: 12,
    band: "high",
    uuid: "a1000001-0000-4000-8000-000000000004",
    subject: "Algebra",
    name: "Vertex form of a quadratic",
    difficulty: "challenge",
    tags: ["quadratic", "vertex", "parabola"],
    expectedAnswer: "2(x-2)^2-3",
    acceptedAnswers: [
      "2(x - 2)^2 - 3",
      "f(x)=2(x-2)^2-3",
      "f(x) = 2(x - 2)^2 - 3",
    ],
    prompt: [
      "Vertex form of a quadratic",
      "A quadratic function is given by f(x) = 2x^2 - 8x + 5",
      "1. Write the function in vertex form.",
      "2. State the coordinates of the vertex.",
      "3. Determine the axis of symmetry.",
      "4. Find the y-intercept.",
      "5. Sketch a rough graph of the parabola.",
    ].join("\n"),
    hints: [
      "Factor 2 out of the x terms before completing the square.",
      "The vertex is (h, k) from a(x − h)² + k.",
    ],
    explanation:
      "Complete the square: 2(x² − 4x) + 5 = 2((x − 2)² − 4) + 5 = 2(x − 2)² − 3.",
    parts: [
      {
        id: "1",
        text: "Write the function in vertex form.",
        expectedAnswer: "2(x-2)^2-3",
        acceptedAnswers: ["2(x - 2)^2 - 3", "f(x)=2(x-2)^2-3"],
        hints: [
          "Factor 2 out of the x terms before completing the square.",
        ],
      },
      {
        id: "2",
        text: "State the coordinates of the vertex.",
        expectedAnswer: "(2, -3)",
        acceptedAnswers: ["(2,-3)", "vertex (2, -3)"],
        hints: ["The vertex is (h, k) from a(x − h)² + k."],
      },
      {
        id: "3",
        text: "Determine the axis of symmetry.",
        expectedAnswer: "x = 2",
        acceptedAnswers: ["x=2"],
        hints: ["The axis of symmetry is the vertical line through the vertex."],
      },
      {
        id: "4",
        text: "Find the y-intercept.",
        expectedAnswer: "(0, 5)",
        acceptedAnswers: ["(0,5)", "f(0)=5", "f(0) = 5"],
        hints: [
          "The y-intercept is f(0). Write it as a point on the y-axis, not only y = a number.",
        ],
      },
      {
        id: "5",
        text: "Sketch a rough graph of the parabola.",
        expectedAnswer: "parabola opens up",
        acceptedAnswers: ["opens up", "minimum at (2, -3)"],
        hints: [
          "Plot the vertex, axis, and y-intercept. The parabola opens the same way as the sign of a.",
        ],
      },
    ],
    notations: [
      text(2, 44, 1, 2, "A quadratic function is given by f(x) = 2x² − 8x + 5"),
      text(2, 40, 4, 5, "1. Write the function in vertex form."),
      text(2, 40, 7, 8, "2. State the coordinates of the vertex."),
      text(2, 40, 10, 11, "3. Determine the axis of symmetry."),
      text(2, 36, 13, 14, "4. Find the y-intercept."),
      text(2, 44, 16, 17, "5. Sketch a rough graph of the parabola."),
    ],
  },
  {
    id: "geometry-triangle-area",
    skillId: "geometry.triangle-area",
    gradeMin: 5,
    gradeMax: 6,
    band: "elementary",
    uuid: "a1000002-0000-4000-8000-000000000001",
    subject: "Geometry",
    name: "Area of a triangle",
    difficulty: "intro",
    tags: ["area", "triangle"],
    expectedAnswer: "24",
    acceptedAnswers: ["24 square units", "area = 24"],
    prompt:
      "Area of a triangle\nFind the area of the triangle.\nGiven:\nbase=8\nh = 6",
    hints: [
      "Area of a triangle is one-half base times height.",
      "Here the base is 8 and the height is 6.",
    ],
    explanation: "A = (1/2) × 8 × 6 = 24.",
    notations: [
      text(2, 38, 1, 2, "Find the area of the triangle."),
      line(10, 24 - 150 / 33, 30, 24 - 150 / 33),
      line(10, 24 - 150 / 33, 20, 10 - 150 / 33),
      line(20, 10 - 150 / 33, 30, 24 - 150 / 33),
      ann(19, 25 - 150 / 33, "base=8"),
      ann(21, 17 - 150 / 33, "h = 6"),
    ],
  },
  {
    id: "geometry-pythagorean-hypotenuse",
    skillId: "geometry.pythagorean",
    gradeMin: 8,
    gradeMax: 9,
    band: "middle",
    uuid: "a1000002-0000-4000-8000-000000000002",
    subject: "Geometry",
    name: "Pythagorean theorem — find the hypotenuse",
    difficulty: "core",
    tags: ["pythagorean", "right-triangle"],
    expectedAnswer: "5",
    acceptedAnswers: ["c = 5", "c=5"],
    prompt:
      "Pythagorean theorem — find the hypotenuse\nIn △ABC, find the length of hypotenuse c.\nGiven:\n∠C = 90°\na = 3\nb = 4\nc = ?",
    hints: [
      "In a right triangle, a² + b² = c².",
      "Square both legs, add, then take the square root.",
    ],
    explanation: "3² + 4² = 9 + 16 = 25, so c = 5.",
    notations: [
      text(2, 44, 1, 2, "In △ABC, find the length of hypotenuse c."),
      line(8, 22, 32, 22),
      line(8, 22, 8, 8),
      line(8, 8, 32, 22),
      sym(6, 22, "C"),
      sym(33, 22, "B"),
      sym(6, 7, "A"),
      ann(9, 22, "∠C = 90°"),
      ann(6, 15, "a = 3"),
      ann(19, 23, "b = 4"),
      ann(21, 14, "c = ?"),
    ],
  },
  {
    id: "geometry-circle-circumference",
    skillId: "geometry.circle-circumference",
    gradeMin: 8,
    gradeMax: 9,
    band: "middle",
    uuid: "a1000002-0000-4000-8000-000000000003",
    subject: "Geometry",
    name: "Circumference of a circle",
    difficulty: "core",
    tags: ["circle", "circumference"],
    expectedAnswer: "10π",
    acceptedAnswers: ["10pi", "10π units", "C = 10π", "C=10π"],
    prompt:
      "Circumference of a circle\nThe circle has radius 5. Find the circumference. Leave π in the answer.\nGiven:\nr = 5",
    hints: [
      "Circumference is 2πr.",
      "Keep π in the answer instead of using a decimal.",
    ],
    explanation: "C = 2πr = 2π × 5 = 10π.",
    notations: [
      text(2, 44, 1, 2, "The circle has radius 5. Find the circumference."),
      circle(20, 16, 8),
      line(20, 16, 28, 16),
      ann(24, 15, "r = 5"),
    ],
  },
  {
    id: "trig-exact-values-30-60",
    skillId: "trig.exact-30-60",
    gradeMin: 10,
    gradeMax: 11,
    band: "high",
    uuid: "a1000003-0000-4000-8000-000000000001",
    subject: "Trigonometry",
    name: "sin(30°) and cos(60°)",
    difficulty: "intro",
    tags: ["exact-values", "unit-circle"],
    expectedAnswer: "sin(30°)=1/2, cos(60°)=1/2",
    acceptedAnswers: ["1/2 and 1/2", "sin 30 = 1/2, cos 60 = 1/2"],
    prompt:
      "sin(30°) and cos(60°)\nFind exact values (no calculator):\nsin(30°) =\ncos(60°) =",
    hints: [
      "These are special angles from a 30-60-90 triangle.",
      "sin 30° and cos 60° are the same exact value.",
    ],
    explanation:
      "In a 30-60-90 triangle the side opposite 30° is half the hypotenuse, so sin 30° = 1/2. The side adjacent to 60° is that same half, so cos 60° = 1/2 as well.",
    notations: [
      text(2, 36, 1, 2, "Find exact values (no calculator):"),
      text(2, 28, 4, 5, "sin(30°) ="),
      text(2, 28, 7, 8, "cos(60°) ="),
    ],
  },
  {
    id: "trig-right-triangle-ratios",
    skillId: "trig.right-ratio",
    gradeMin: 10,
    gradeMax: 11,
    band: "high",
    uuid: "a1000003-0000-4000-8000-000000000002",
    subject: "Trigonometry",
    name: "Solve a right triangle",
    difficulty: "core",
    tags: ["trig-ratios", "right-triangle"],
    expectedAnswer: "sin θ = 3/5, cos θ = 4/5, tan θ = 3/4",
    acceptedAnswers: ["sin=3/5, cos=4/5, tan=3/4"],
    prompt:
      "Solve a right triangle\nFor angle θ at B, find sin θ, cos θ, and tan θ.\nGiven:\n90°\nθ\nopp = 3\nadj = 4\nhyp = 5",
    hints: [
      "SOH-CAH-TOA: sine is opposite over hypotenuse.",
      "θ is at B, so the side opposite θ is AC = 3.",
    ],
    explanation: "sin θ = 3/5, cos θ = 4/5, tan θ = 3/4.",
    notations: [
      text(2, 40, 1, 2, "For angle θ at B, find sin θ, cos θ, and tan θ."),
      line(8, 22, 32, 22),
      line(8, 22, 8, 8),
      line(8, 8, 32, 22),
      sym(6, 22, "C"),
      sym(33, 22, "B"),
      sym(6, 7, "A"),
      ann(9, 22, "90°"),
      ann(31, 20, "θ"),
      ann(6, 15, "opp = 3"),
      ann(19, 23, "adj = 4"),
      ann(21, 14, "hyp = 5"),
    ],
  },
  {
    id: "trig-unit-circle-cos-0",
    skillId: "trig.exact-axis",
    gradeMin: 10,
    gradeMax: 11,
    band: "high",
    uuid: "a1000003-0000-4000-8000-000000000003",
    subject: "Trigonometry",
    name: "cos(0°) on the unit circle",
    difficulty: "intro",
    tags: ["unit-circle", "exact-values"],
    expectedAnswer: "1",
    acceptedAnswers: ["cos(0°)=1", "cos 0 = 1"],
    prompt:
      "cos(0°) on the unit circle\nOn the unit circle, find the exact value of cos(0°).",
    hints: [
      "Cosine is the x-coordinate of the point on the unit circle.",
      "0° is the point (1, 0).",
    ],
    explanation:
      "0° starts at the positive x-axis of the unit circle, the point (1, 0). Cosine reads the x-coordinate, so cos 0° = 1. Sine would read the y-coordinate, which is 0.",
    notations: [
      text(2, 44, 1, 2, "On the unit circle, find the exact value of cos(0°)."),
      circle(22, 16, 7),
      line(14, 16, 30, 16),
      line(22, 9, 22, 23),
      ann(29, 15, "(1, 0)"),
    ],
  },
  {
    id: "calculus-derivative-power",
    skillId: "calc.power-rule",
    gradeMin: 11,
    gradeMax: 12,
    band: "high",
    uuid: "a1000004-0000-4000-8000-000000000001",
    subject: "Calculus",
    name: "Derivative of x³",
    difficulty: "intro",
    tags: ["derivative", "power-rule"],
    expectedAnswer: "3x²",
    acceptedAnswers: ["3x^2", "3x2"],
    prompt: "Derivative of x³\nDifferentiate with respect to x:\nd/dx(x^3)",
    hints: [
      "Power rule: bring the exponent down and subtract one from it.",
      "The derivative of xⁿ is n xⁿ⁻¹.",
    ],
    explanation:
      "The power rule multiplies by the exponent and then lowers it by one. The exponent on x³ is 3, so the derivative is 3x².",
    notations: [
      text(2, 32, 1, 2, "Differentiate with respect to x:"),
      sym(2, 5, "d"),
      sym(3, 5, "/"),
      sym(4, 5, "d"),
      sym(5, 5, "x"),
      sym(6, 5, "("),
      sym(7, 5, "x"),
      exp(8, 5, "3"),
      sym(9, 5, ")"),
    ],
  },
  {
    id: "calculus-integral-power",
    skillId: "calc.indefinite-integral",
    gradeMin: 11,
    gradeMax: 12,
    band: "high",
    uuid: "a1000004-0000-4000-8000-000000000002",
    subject: "Calculus",
    name: "Evaluate ∫ x dx",
    difficulty: "core",
    tags: ["integral", "indefinite-integral"],
    expectedAnswer: "(1/2)x² + C",
    acceptedAnswers: [
      "x²/2 + C",
      "x^2/2 + C",
      "(1/2)x^2 + C",
      "(x^2)/(2) + C",
      "(x²)/(2) + C",
    ],
    prompt: "Evaluate ∫ x dx\nEvaluate the indefinite integral:\n∫x dx",
    hints: [
      "Reverse the power rule: raise the power by 1, then divide by the new power.",
      "Indefinite integrals need a constant of integration.",
    ],
    explanation:
      "Raise the power of x from 1 to 2, then divide by the new power. That antiderivative is x²/2, which is the same as (1/2)x². An indefinite integral also needs + C, because the derivative of a constant is 0.",
    notations: [
      text(2, 32, 1, 2, "Evaluate the indefinite integral:"),
      sym(2, 5, "∫"),
      sym(3, 5, "x"),
      sym(4, 5, "d"),
      sym(5, 5, "x"),
    ],
  },
  {
    id: "calculus-chain-rule",
    skillId: "calc.chain-rule",
    gradeMin: 11,
    gradeMax: 12,
    band: "high",
    uuid: "a1000004-0000-4000-8000-000000000003",
    subject: "Calculus",
    name: "Chain rule for (2x + 1)³",
    difficulty: "challenge",
    tags: ["derivative", "chain-rule"],
    expectedAnswer: "6(2x+1)^2",
    acceptedAnswers: [
      "6(2x + 1)^2",
      "6(2x+1)²",
      "3(2x+1)^2 * 2",
      "3(2x+1)^2·2",
    ],
    prompt:
      "Chain rule for (2x + 1)³\nDifferentiate with respect to x:\n(2x+1)^3",
    hints: [
      "The chain rule is outer derivative times inner derivative.",
      "The inner function is 2x + 1, whose derivative is 2.",
    ],
    explanation:
      "The outside power is 3, so the outer derivative is 3(2x + 1)². The inside derivative of 2x + 1 is 2. Multiply those factors: 3 · 2 · (2x + 1)² = 6(2x + 1)².",
    notations: [
      text(2, 36, 1, 2, "Differentiate with respect to x:"),
      sym(2, 5, "("),
      sym(3, 5, "2"),
      sym(4, 5, "x"),
      sym(5, 5, "+"),
      sym(6, 5, "1"),
      sym(7, 5, ")"),
      exp(8, 5, "3"),
    ],
  },
  {
    id: "statistics-mean",
    skillId: "stats.mean",
    gradeMin: 5,
    gradeMax: 6,
    band: "elementary",
    uuid: "a1000005-0000-4000-8000-000000000001",
    subject: "Statistics",
    name: "Mean of a data set",
    difficulty: "intro",
    tags: ["mean", "average"],
    expectedAnswer: "8",
    acceptedAnswers: ["mean = 8"],
    prompt: "Mean of a data set\nFind the mean of the data set:\n4,  6,  8,  10,  12",
    hints: [
      "Mean is the sum of the values divided by how many there are.",
      "There are 5 numbers in this list.",
    ],
    explanation: "(4 + 6 + 8 + 10 + 12) / 5 = 40 / 5 = 8.",
    notations: [
      text(2, 40, 1, 2, "Find the mean of the data set:"),
      text(2, 24, 4, 5, "4,  6,  8,  10,  12"),
      ...dataDotPlotFigure([4, 6, 8, 10, 12]),
    ],
  },
  {
    id: "statistics-independent-events",
    skillId: "stats.independent-events",
    gradeMin: 8,
    gradeMax: 9,
    band: "middle",
    uuid: "a1000005-0000-4000-8000-000000000002",
    subject: "Statistics",
    name: "Probability of two independent events",
    difficulty: "challenge",
    tags: ["probability", "independent"],
    expectedAnswer: "1/8",
    acceptedAnswers: ["0.125", "P = 1/8"],
    prompt:
      "Probability of two independent events\nA fair coin is tossed twice, then a fair die is rolled.\nEvents are independent. Find P(heads, heads, even).",
    hints: [
      "Independent events: multiply the separate probabilities.",
      "P(heads) = 1/2 and P(even on a die) = 1/2.",
    ],
    explanation: "(1/2) × (1/2) × (1/2) = 1/8.",
    notations: [
      text(2, 44, 1, 3, "A fair coin is tossed twice, then a fair die is rolled."),
      text(2, 44, 5, 6, "Events are independent. Find P(heads, heads, even)."),
    ],
  },
  {
    id: "statistics-median",
    skillId: "stats.median",
    gradeMin: 5,
    gradeMax: 6,
    band: "elementary",
    uuid: "a1000005-0000-4000-8000-000000000003",
    subject: "Statistics",
    name: "Median of a data set",
    difficulty: "intro",
    tags: ["median", "center"],
    expectedAnswer: "7",
    acceptedAnswers: ["median = 7"],
    prompt: "Median of a data set\nFind the median of the data set:\n3,  12,  4,  9,  7",
    hints: [
      "Put the numbers in order first.",
      "The median is the middle value of the ordered list.",
    ],
    explanation: "Ordered: 3, 4, 7, 9, 12. The middle value is 7.",
    notations: [
      text(2, 40, 1, 2, "Find the median of the data set:"),
      text(2, 24, 4, 5, "3,  12,  4,  9,  7"),
      ...dataDotPlotFigure([3, 12, 4, 9, 7]),
    ],
  },
  {
    id: "general-order-of-operations",
    skillId: "arithmetic.pemdas",
    gradeMin: 5,
    gradeMax: 6,
    band: "elementary",
    uuid: "a1000006-0000-4000-8000-000000000001",
    subject: "General",
    name: "Order of operations",
    difficulty: "intro",
    tags: ["pemdas", "arithmetic"],
    expectedAnswer: "11",
    acceptedAnswers: ["3 + 8 = 11"],
    prompt: "Order of operations\nEvaluate:\n3+4×2",
    hints: [
      "Multiply before you add.",
      "4 × 2 is 8, then add 3.",
    ],
    explanation: "3 + 4 × 2 = 3 + 8 = 11.",
    notations: [
      text(2, 30, 1, 2, "Evaluate:"),
      sym(2, 5, "3"),
      sym(3, 5, "+"),
      sym(4, 5, "4"),
      sym(5, 5, "×"),
      sym(6, 5, "2"),
    ],
  },
  {
    id: "general-percent-increase",
    skillId: "arithmetic.percent-increase",
    gradeMin: 7,
    gradeMax: 8,
    band: "middle",
    uuid: "a1000006-0000-4000-8000-000000000002",
    subject: "General",
    name: "Percent increase and decrease",
    difficulty: "core",
    tags: ["percent", "word-problem"],
    expectedAnswer: "25%",
    acceptedAnswers: ["25 percent", "0.25"],
    prompt:
      "Percent increase and decrease\nA store price increases from $40 to $50.\nWhat is the percent increase?",
    hints: [
      "Percent increase is change divided by the original amount.",
      "The change is 10, and the original price is 40.",
    ],
    explanation: "(50 − 40) / 40 = 10/40 = 25%.",
    notations: [
      text(2, 44, 1, 3, "A store price increases from $40 to $50."),
      text(2, 36, 5, 6, "What is the percent increase?"),
    ],
  },
  {
    id: "general-fraction-of-quantity",
    skillId: "arithmetic.fraction-of",
    gradeMin: 5,
    gradeMax: 6,
    band: "elementary",
    uuid: "a1000006-0000-4000-8000-000000000003",
    subject: "General",
    name: "Fraction of a quantity",
    difficulty: "intro",
    tags: ["fractions", "word-problem"],
    expectedAnswer: "15",
    acceptedAnswers: ["15 apples", "3/4 of 20 = 15"],
    prompt:
      "Fraction of a quantity\nA bag has 20 apples. What is 3/4 of 20?",
    hints: [
      "Of means multiply: (3/4) × 20.",
      "You can also take 20 ÷ 4, then multiply by 3.",
    ],
    explanation: "(3/4) × 20 = 15.",
    notations: [
      text(2, 40, 1, 2, "A bag has 20 apples. What is 3/4 of 20?"),
    ],
  },
  {
    id: "algebra-linear-inequality",
    skillId: "algebra.linear-inequality",
    gradeMin: 8,
    gradeMax: 9,
    band: "middle",
    uuid: "a1000001-0000-4000-8000-000000000005",
    subject: "Algebra",
    name: "Solve 3x − 5 > 7",
    difficulty: "core",
    tags: ["inequality", "linear"],
    expectedAnswer: "x > 4",
    acceptedAnswers: ["x>4", "x > 4"],
    prompt: "Solve 3x − 5 > 7\nSolve the inequality:\n3x-5>7",
    hints: [
      "Add 5 to both sides first, same as an equation.",
      "Then divide by 3. The inequality direction stays the same.",
    ],
    explanation: "3x > 12, so x > 4.",
    notations: [
      text(2, 32, 1, 2, "Solve the inequality:"),
      text(2, 20, 4, 5, "3x − 5 > 7"),
    ],
  },
  {
    id: "algebra-expand-binomial",
    skillId: "algebra.expand-binomial",
    gradeMin: 7,
    gradeMax: 8,
    band: "middle",
    uuid: "a1000001-0000-4000-8000-000000000006",
    subject: "Algebra",
    name: "Expand (x + 2)(x + 3)",
    difficulty: "intro",
    tags: ["expand", "binomial"],
    expectedAnswer: "x^2 + 5x + 6",
    acceptedAnswers: ["x² + 5x + 6", "x^2+5x+6", "x²+5x+6"],
    prompt: "Expand (x + 2)(x + 3)\nExpand:\n(x+2)(x+3)",
    hints: [
      "Use FOIL or distribute each term in the first factor.",
      "The middle terms are 3x and 2x.",
    ],
    explanation: "x² + 3x + 2x + 6 = x² + 5x + 6.",
    notations: [
      text(2, 24, 1, 2, "Expand:"),
      text(2, 22, 4, 5, "(x + 2)(x + 3)"),
    ],
  },
  {
    id: "algebra-slope-two-points",
    skillId: "algebra.slope",
    gradeMin: 8,
    gradeMax: 9,
    band: "middle",
    uuid: "a1000001-0000-4000-8000-000000000007",
    subject: "Algebra",
    name: "Slope through (1, 2) and (3, 8)",
    difficulty: "core",
    tags: ["slope", "linear"],
    expectedAnswer: "3",
    acceptedAnswers: ["m = 3", "m=3", "6/2"],
    prompt:
      "Slope through (1, 2) and (3, 8)\nFind the slope of the line through (1, 2) and (3, 8).",
    hints: [
      "Slope is rise over run: (y₂ − y₁) / (x₂ − x₁).",
      "The y change is 6 and the x change is 2.",
    ],
    explanation: "m = (8 − 2) / (3 − 1) = 6/2 = 3.",
    notations: [
      text(2, 44, 1, 2, "Find the slope of the line through (1, 2) and (3, 8)."),
    ],
  },
  {
    id: "geometry-triangle-angle-sum",
    skillId: "geometry.angle-sum",
    gradeMin: 5,
    gradeMax: 6,
    band: "elementary",
    uuid: "a1000002-0000-4000-8000-000000000004",
    subject: "Geometry",
    name: "Missing angle in a triangle",
    difficulty: "intro",
    tags: ["triangle", "angles"],
    expectedAnswer: "70°",
    acceptedAnswers: ["70", "70 degrees"],
    prompt:
      "Missing angle in a triangle\nA triangle has angles 50° and 60°. Find the third angle.",
    hints: [
      "The three angles of a triangle add to 180°.",
      "Subtract 50° and 60° from 180°.",
    ],
    explanation: "180° − 50° − 60° = 70°.",
    notations: [
      text(2, 44, 1, 2, "A triangle has angles 50° and 60°. Find the third angle."),
      line(10, 15, 30, 15),
      line(10, 15, 20, 1),
      line(20, 1, 30, 15),
      ann(14, 13, "50°"),
      ann(24, 13, "60°"),
      ann(19, 5, "?"),
    ],
  },
  {
    id: "geometry-circle-area",
    skillId: "geometry.circle-area",
    gradeMin: 8,
    gradeMax: 9,
    band: "middle",
    uuid: "a1000002-0000-4000-8000-000000000005",
    subject: "Geometry",
    name: "Area of a circle",
    difficulty: "core",
    tags: ["circle", "area"],
    expectedAnswer: "9π",
    acceptedAnswers: ["9pi", "9π square units", "A = 9π", "A=9π"],
    prompt:
      "Area of a circle\nThe circle has radius 3. Find the area. Leave π in the answer.\nGiven:\nr = 3",
    hints: [
      "Area of a circle is πr².",
      "Keep π in the answer instead of using a decimal.",
    ],
    explanation: "A = πr² = π × 3² = 9π.",
    notations: [
      text(2, 40, 1, 2, "The circle has radius 3. Find the area."),
      circle(20, 16, 6),
      line(20, 16, 26, 16),
      ann(22, 15, "r = 3"),
    ],
  },
  {
    id: "geometry-similar-triangles",
    skillId: "geometry.similar-triangles",
    gradeMin: 10,
    gradeMax: 11,
    band: "high",
    uuid: "a1000002-0000-4000-8000-000000000006",
    subject: "Geometry",
    name: "Similar triangles — missing side",
    difficulty: "core",
    tags: ["similar", "proportion"],
    expectedAnswer: "10",
    acceptedAnswers: ["x = 10", "x=10"],
    prompt:
      "Similar triangles — missing side\nThe triangles are similar. Matching sides 4 and 8, and 5 and x. Find x.",
    hints: [
      "Similar triangles have proportional matching sides.",
      "The scale factor from 4 to 8 is 2.",
    ],
    explanation: "8/4 = 2, so x = 2 × 5 = 10.",
    notations: [
      text(2, 44, 1, 2, "The triangles are similar. Find x."),
      line(8, 22, 18, 22),
      line(8, 22, 13, 12),
      line(13, 12, 18, 22),
      ann(12, 23, "4"),
      ann(16, 16, "5"),
      line(24, 22, 44, 22),
      line(24, 22, 34, 8),
      line(34, 8, 44, 22),
      ann(33, 23, "8"),
      ann(40, 14, "x"),
    ],
  },
  {
    id: "trig-tan-45",
    skillId: "trig.tan-45",
    gradeMin: 10,
    gradeMax: 11,
    band: "high",
    uuid: "a1000003-0000-4000-8000-000000000004",
    subject: "Trigonometry",
    name: "tan(45°)",
    difficulty: "intro",
    tags: ["exact-values", "tangent"],
    expectedAnswer: "1",
    acceptedAnswers: ["tan(45°)=1", "tan 45 = 1"],
    prompt: "tan(45°)\nFind the exact value of tan(45°). No calculator.",
    hints: [
      "tan θ = sin θ / cos θ.",
      "At 45° sine and cosine are equal.",
    ],
    explanation:
      "Tangent is sine divided by cosine. At 45° the opposite and adjacent sides are equal, so sine and cosine are equal and their quotient is 1. tan 45° = 1.",
    notations: [
      text(2, 40, 1, 2, "Find the exact value of tan(45°). No calculator."),
    ],
  },
  {
    id: "trig-sin-90",
    skillId: "trig.exact-axis",
    gradeMin: 10,
    gradeMax: 11,
    band: "high",
    uuid: "a1000003-0000-4000-8000-000000000005",
    subject: "Trigonometry",
    name: "sin(90°)",
    difficulty: "intro",
    tags: ["exact-values", "unit-circle"],
    expectedAnswer: "1",
    acceptedAnswers: ["sin(90°)=1", "sin 90 = 1"],
    prompt: "sin(90°)\nOn the unit circle, find the exact value of sin(90°).",
    hints: [
      "Sine is the y-coordinate of the point on the unit circle.",
      "90° is the top of the circle, the point (0, 1).",
    ],
    explanation:
      "90° is the top of the unit circle, the point (0, 1). Sine reads the y-coordinate, so sin 90° = 1. Cosine reads the x-coordinate, which is 0 there.",
    notations: [
      text(2, 44, 1, 2, "On the unit circle, find the exact value of sin(90°)."),
      circle(22, 16, 7),
      line(14, 16, 30, 16),
      line(22, 9, 22, 23),
      ann(23, 9, "(0, 1)"),
    ],
  },
  {
    id: "trig-acute-from-sine",
    skillId: "trig.inverse-special",
    gradeMin: 10,
    gradeMax: 11,
    band: "high",
    uuid: "a1000003-0000-4000-8000-000000000006",
    subject: "Trigonometry",
    name: "Acute angle with sin θ = 1/2",
    difficulty: "core",
    tags: ["inverse", "special-angles"],
    expectedAnswer: "30°",
    acceptedAnswers: ["30", "θ = 30°", "theta = 30"],
    prompt:
      "Acute angle with sin θ = 1/2\nθ is acute and sin θ = 1/2. Find θ.",
    hints: [
      "This is a special angle from a 30-60-90 triangle.",
      "sin 30° = 1/2. Acute means between 0° and 90°.",
    ],
    explanation:
      "sin 30° = 1/2, from the short leg of a 30-60-90 triangle. Inverse sine of a positive ratio, in the usual acute range, returns that angle. θ = 30°.",
    notations: [
      text(2, 40, 1, 2, "θ is acute and sin θ = 1/2. Find θ."),
    ],
  },
  {
    id: "calculus-derivative-5x2",
    skillId: "calc.power-rule",
    gradeMin: 11,
    gradeMax: 12,
    band: "high",
    uuid: "a1000004-0000-4000-8000-000000000004",
    subject: "Calculus",
    name: "Derivative of 5x²",
    difficulty: "intro",
    tags: ["derivative", "power-rule"],
    expectedAnswer: "10x",
    acceptedAnswers: ["10 x", "d/dx = 10x"],
    prompt: "Derivative of 5x²\nDifferentiate with respect to x:\n5x^2",
    hints: [
      "Power rule: n xⁿ⁻¹, then multiply by the 5 in front.",
      "The derivative of x² is 2x.",
    ],
    explanation:
      "The constant 5 stays in front. The power rule turns x² into 2x. Then 5 · 2x = 10x, so the derivative is 10x.",
    notations: [
      text(2, 32, 1, 2, "Differentiate with respect to x:"),
      sym(2, 5, "5"),
      sym(3, 5, "x"),
      exp(4, 5, "2"),
    ],
  },
  {
    id: "calculus-product-rule",
    skillId: "calc.product-rule",
    gradeMin: 11,
    gradeMax: 12,
    band: "high",
    uuid: "a1000004-0000-4000-8000-000000000005",
    subject: "Calculus",
    name: "Product rule for x(x + 1)",
    difficulty: "core",
    tags: ["derivative", "product-rule"],
    expectedAnswer: "2x + 1",
    acceptedAnswers: ["2x+1", "1 + 2x"],
    prompt:
      "Product rule for x(x + 1)\nDifferentiate with respect to x:\nx(x+1)",
    hints: [
      "Product rule: u′v + uv′.",
      "You can also expand first: x² + x, then differentiate.",
    ],
    explanation:
      "Expand first: x(x + 1) = x² + x, and the derivative of that is 2x + 1. By the product rule the same result is 1·(x + 1) + x·1 = x + 1 + x = 2x + 1.",
    notations: [
      text(2, 32, 1, 2, "Differentiate with respect to x:"),
      text(2, 18, 4, 5, "x(x + 1)"),
    ],
  },
  {
    id: "calculus-definite-integral",
    skillId: "calc.definite-integral",
    gradeMin: 11,
    gradeMax: 12,
    band: "high",
    uuid: "a1000004-0000-4000-8000-000000000006",
    subject: "Calculus",
    name: "Evaluate ∫₀² 2x dx",
    difficulty: "core",
    tags: ["integral", "definite-integral"],
    expectedAnswer: "4",
    acceptedAnswers: ["4 units", "value = 4"],
    prompt:
      "Evaluate ∫₀² 2x dx\nEvaluate the definite integral from 0 to 2 of 2x dx.",
    hints: [
      "The antiderivative of 2x is x².",
      "Plug in the top limit, then subtract the bottom limit.",
    ],
    explanation:
      "An antiderivative of 2x is x². Evaluate it at the upper limit and subtract the value at the lower limit: 2² − 0² = 4 − 0 = 4.",
    notations: [
      text(2, 44, 1, 2, "Evaluate the definite integral from 0 to 2 of 2x dx."),
      sym(2, 5, "∫"),
      sym(3, 5, "2"),
      sym(4, 5, "x"),
      sym(5, 5, "d"),
      sym(6, 5, "x"),
    ],
  },
  {
    id: "statistics-range",
    skillId: "stats.range",
    gradeMin: 5,
    gradeMax: 6,
    band: "elementary",
    uuid: "a1000005-0000-4000-8000-000000000004",
    subject: "Statistics",
    name: "Range of a data set",
    difficulty: "intro",
    tags: ["range", "spread"],
    expectedAnswer: "9",
    acceptedAnswers: ["range = 9"],
    prompt: "Range of a data set\nFind the range of the data set:\n3,  12,  4,  9,  7",
    hints: [
      "Range is the largest value minus the smallest.",
      "The largest is 12 and the smallest is 3.",
    ],
    explanation: "12 − 3 = 9.",
    notations: [
      text(2, 40, 1, 2, "Find the range of the data set:"),
      text(2, 24, 4, 5, "3,  12,  4,  9,  7"),
      ...dataDotPlotFigure([3, 12, 4, 9, 7]),
    ],
  },
  {
    id: "statistics-mode",
    skillId: "stats.mode",
    gradeMin: 5,
    gradeMax: 6,
    band: "elementary",
    uuid: "a1000005-0000-4000-8000-000000000005",
    subject: "Statistics",
    name: "Mode of a data set",
    difficulty: "intro",
    tags: ["mode", "center"],
    expectedAnswer: "5",
    acceptedAnswers: ["mode = 5"],
    prompt: "Mode of a data set\nFind the mode of the data set:\n2,  5,  3,  5,  7,  5,  2",
    hints: [
      "The mode is the value that appears most often.",
      "Count how many times each number shows up.",
    ],
    explanation: "5 appears three times, more than any other value.",
    notations: [
      text(2, 40, 1, 2, "Find the mode of the data set:"),
      text(2, 28, 4, 5, "2,  5,  3,  5,  7,  5,  2"),
      ...dataDotPlotFigure([2, 5, 3, 5, 7, 5, 2]),
    ],
  },
  {
    id: "statistics-die-even",
    skillId: "stats.sample-space",
    gradeMin: 5,
    gradeMax: 6,
    band: "elementary",
    uuid: "a1000005-0000-4000-8000-000000000006",
    subject: "Statistics",
    name: "Probability of an even roll",
    difficulty: "intro",
    tags: ["probability", "sample-space"],
    expectedAnswer: "1/2",
    acceptedAnswers: ["0.5", "3/6", "P = 1/2"],
    prompt:
      "Probability of an even roll\nA fair six-sided die is rolled. Find P(even).",
    hints: [
      "Write the probability of an even face as a fraction: the number of even faces over the number of sides.",
      "The even faces are 2, 4, and 6.",
    ],
    explanation: "3 even faces out of 6, so 3/6 = 1/2.",
    notations: [
      text(2, 44, 1, 2, "A fair six-sided die is rolled. Find P(even)."),
    ],
  },
  {
    id: "general-power-of-two",
    skillId: "arithmetic.power-of-two",
    gradeMin: 5,
    gradeMax: 6,
    band: "elementary",
    uuid: "a1000006-0000-4000-8000-000000000004",
    subject: "General",
    name: "Evaluate 2⁵",
    difficulty: "intro",
    tags: ["exponents", "arithmetic"],
    expectedAnswer: "32",
    acceptedAnswers: ["2^5 = 32"],
    prompt: "Evaluate 2⁵\nEvaluate:\n2^5",
    hints: [
      "2⁵ means five factors of 2 multiplied together.",
      "2 × 2 × 2 × 2 × 2.",
    ],
    explanation: "2⁵ = 32.",
    notations: [
      text(2, 24, 1, 2, "Evaluate:"),
      sym(2, 5, "2"),
      exp(3, 5, "5"),
    ],
  },
  {
    id: "general-percent-of",
    skillId: "arithmetic.percent-of",
    gradeMin: 5,
    gradeMax: 6,
    band: "elementary",
    uuid: "a1000006-0000-4000-8000-000000000005",
    subject: "General",
    name: "15% of 80",
    difficulty: "intro",
    tags: ["percent", "arithmetic"],
    expectedAnswer: "12",
    acceptedAnswers: ["12.0", "15% of 80 = 12"],
    prompt: "15% of 80\nWhat is 15% of 80?",
    hints: [
      "Percent means hundredths: 15% = 15/100.",
      "You can also take 10% of 80 and half of that, then add.",
    ],
    explanation: "0.15 × 80 = 12.",
    notations: [
      text(2, 28, 1, 2, "What is 15% of 80?"),
    ],
  },
  {
    id: "general-ratio-parts",
    skillId: "arithmetic.ratio-parts",
    gradeMin: 7,
    gradeMax: 8,
    band: "middle",
    uuid: "a1000006-0000-4000-8000-000000000006",
    subject: "General",
    name: "Share in the ratio 3:5",
    difficulty: "core",
    tags: ["ratio", "word-problem"],
    expectedAnswer: "15",
    acceptedAnswers: ["15 dollars", "$15"],
    prompt:
      "Share in the ratio 3:5\n$40 is shared in the ratio 3:5. How much is the smaller share?",
    hints: [
      "The ratio 3:5 has 3 + 5 = 8 parts.",
      "The smaller share is 3 of those 8 parts.",
    ],
    explanation: "Each part is 40/8 = 5, so 3 parts is 15.",
    notations: [
      text(2, 44, 1, 2, "$40 is shared in the ratio 3:5. How much is the smaller share?"),
    ],
  },
  {
    id: "vectors-add-components",
    skillId: "vectors.add",
    gradeMin: 11,
    gradeMax: 12,
    band: "high",
    uuid: "a1000007-0000-4000-8000-000000000001",
    subject: "Vectors",
    name: "Add ⟨3, 1⟩ + ⟨2, 4⟩",
    difficulty: "intro",
    tags: ["vectors", "components"],
    expectedAnswer: "⟨5, 5⟩",
    acceptedAnswers: [
      "<5, 5>",
      "<5,5>",
      "(5, 5)",
      "(5,5)",
      "5i + 5j",
      "5i+5j",
    ],
    prompt: "Add ⟨3, 1⟩ + ⟨2, 4⟩\nCompute ⟨3, 1⟩ + ⟨2, 4⟩.",
    hints: [
      "Add matching components: x with x, y with y.",
      "The first components are 3 and 2.",
    ],
    explanation: "⟨3 + 2, 1 + 4⟩ = ⟨5, 5⟩.",
    notations: [
      text(2, 32, 1, 2, "Compute ⟨3, 1⟩ + ⟨2, 4⟩."),
      line(10, 22, 16, 18, { arrowRight: true }),
      ann(16, 17, "a"),
      line(16, 18, 22, 12, { arrowRight: true }),
      ann(22, 11, "b"),
      line(10, 22, 22, 12, { arrowRight: true, dashed: true }),
      ann(22, 13, "a+b"),
    ],
  },
  {
    id: "vectors-magnitude-3-4",
    skillId: "vectors.magnitude",
    gradeMin: 11,
    gradeMax: 12,
    band: "high",
    uuid: "a1000007-0000-4000-8000-000000000002",
    subject: "Vectors",
    name: "Magnitude of ⟨3, 4⟩",
    difficulty: "intro",
    tags: ["vectors", "magnitude"],
    expectedAnswer: "5",
    acceptedAnswers: ["||v|| = 5", "|v| = 5", "5 units"],
    prompt: "Magnitude of ⟨3, 4⟩\nFind the magnitude of ⟨3, 4⟩.",
    hints: [
      "Magnitude is √(x² + y²).",
      "This is a 3-4-5 right triangle.",
    ],
    explanation: "√(3² + 4²) = √(9 + 16) = √25 = 5.",
    notations: [
      text(2, 36, 1, 2, "Find the magnitude of ⟨3, 4⟩."),
      line(10, 22, 22, 22),
      line(22, 22, 22, 10, { arrowRight: true }),
      line(10, 22, 22, 10, { arrowRight: true }),
      ann(15, 23, "3"),
      ann(23, 16, "4"),
      ann(14, 14, "⟨3, 4⟩"),
    ],
  },
  {
    id: "vectors-scalar-multiply",
    skillId: "vectors.scalar",
    gradeMin: 11,
    gradeMax: 12,
    band: "high",
    uuid: "a1000007-0000-4000-8000-000000000003",
    subject: "Vectors",
    name: "Scalar 3⟨2, −1⟩",
    difficulty: "intro",
    tags: ["vectors", "scalar"],
    expectedAnswer: "⟨6, −3⟩",
    acceptedAnswers: ["<6, -3>", "<6,-3>", "(6, -3)", "(6,-3)", "6i - 3j"],
    prompt: "Scalar 3⟨2, −1⟩\nCompute 3⟨2, −1⟩.",
    hints: [
      "Multiply each component by the scalar 3.",
      "The y-component is negative, so it stays negative.",
    ],
    explanation: "⟨3 · 2, 3 · (−1)⟩ = ⟨6, −3⟩.",
    notations: [
      text(2, 28, 1, 2, "Compute 3⟨2, −1⟩."),
    ],
  },
  {
    id: "vectors-dot-product",
    skillId: "vectors.dot",
    gradeMin: 11,
    gradeMax: 12,
    band: "high",
    uuid: "a1000007-0000-4000-8000-000000000004",
    subject: "Vectors",
    name: "Dot product ⟨2, 3⟩ · ⟨4, 1⟩",
    difficulty: "core",
    tags: ["vectors", "dot-product"],
    expectedAnswer: "11",
    acceptedAnswers: ["11", "a·b = 11"],
    prompt: "Dot product ⟨2, 3⟩ · ⟨4, 1⟩\nFind ⟨2, 3⟩ · ⟨4, 1⟩.",
    hints: [
      "The dot product is x₁x₂ + y₁y₂.",
      "Multiply matching components, then add.",
    ],
    explanation: "2·4 + 3·1 = 8 + 3 = 11.",
    notations: [
      text(2, 36, 1, 2, "Find ⟨2, 3⟩ · ⟨4, 1⟩."),
    ],
  },
  {
    id: "complex-i-squared",
    skillId: "complex.powers-of-i",
    gradeMin: 11,
    gradeMax: 12,
    band: "high",
    uuid: "a1000008-0000-4000-8000-000000000001",
    subject: "Complex",
    name: "The imaginary unit i²",
    difficulty: "intro",
    tags: ["imaginary", "powers-of-i"],
    expectedAnswer: "-1",
    acceptedAnswers: ["i^2 = -1", "i² = −1", "−1"],
    prompt: "The imaginary unit i²\nSimplify i².",
    hints: [
      "i is defined so that i² = −1.",
      "This is the definition of the imaginary unit, not a calculator value.",
    ],
    explanation:
      "The imaginary unit is defined by the rule i² = −1. Simplifying i² stops there: the value is −1, not a decimal approximation.",
    notations: [
      text(2, 24, 1, 2, "Simplify i²."),
      sym(2, 5, "i"),
      exp(3, 5, "2"),
    ],
  },
  {
    id: "complex-i-cubed",
    skillId: "complex.powers-of-i",
    gradeMin: 11,
    gradeMax: 12,
    band: "high",
    uuid: "a1000008-0000-4000-8000-000000000002",
    subject: "Complex",
    name: "Simplify i³",
    difficulty: "core",
    tags: ["imaginary", "powers-of-i"],
    expectedAnswer: "-i",
    acceptedAnswers: ["−i", "i^3 = -i", "-1i"],
    prompt: "Simplify i³\nSimplify i³.",
    hints: [
      "Write i³ as i² · i.",
      "i² is −1, so multiply that by i.",
    ],
    explanation:
      "Split the power: i³ = i² · i. Replace i² with −1, then multiply by the remaining factor of i: (−1) · i = −i.",
    notations: [
      text(2, 24, 1, 2, "Simplify i³."),
      sym(2, 5, "i"),
      exp(3, 5, "3"),
    ],
  },
  {
    id: "complex-add",
    skillId: "complex.add",
    gradeMin: 11,
    gradeMax: 12,
    band: "high",
    uuid: "a1000008-0000-4000-8000-000000000003",
    subject: "Complex",
    name: "Add (3 + 2i) + (1 − 4i)",
    difficulty: "intro",
    tags: ["complex", "arithmetic"],
    expectedAnswer: "4 − 2i",
    acceptedAnswers: ["4-2i", "4 - 2i", "-2i + 4"],
    prompt: "Add (3 + 2i) + (1 − 4i)\nCompute (3 + 2i) + (1 − 4i).",
    hints: [
      "Add the real parts and the imaginary parts separately.",
      "The real parts are 3 and 1.",
    ],
    explanation: "(3 + 1) + (2 − 4)i = 4 − 2i.",
    notations: [
      text(2, 36, 1, 2, "Compute (3 + 2i) + (1 − 4i)."),
    ],
  },
  {
    id: "complex-modulus",
    skillId: "complex.modulus",
    gradeMin: 11,
    gradeMax: 12,
    band: "high",
    uuid: "a1000008-0000-4000-8000-000000000004",
    subject: "Complex",
    name: "Modulus of 3 + 4i",
    difficulty: "core",
    tags: ["complex", "modulus"],
    expectedAnswer: "5",
    acceptedAnswers: ["|z| = 5", "|3+4i| = 5"],
    prompt: "Modulus of 3 + 4i\nFind |3 + 4i|.",
    hints: [
      "The modulus |a + bi| is √(a² + b²).",
      "This is the same 3-4-5 triangle as a vector magnitude.",
    ],
    explanation: "|3 + 4i| = √(9 + 16) = √25 = 5.",
    notations: [
      text(2, 28, 1, 2, "Find |3 + 4i|."),
      line(12, 20, 24, 20),
      line(24, 20, 24, 8),
      line(12, 20, 24, 8),
      ann(17, 21, "3"),
      ann(25, 14, "4"),
      ann(13, 12, "3+4i"),
    ],
  },
  {
    id: "algebra-add-one-step",
    skillId: "algebra.linear-one-step",
    gradeMin: 7,
    gradeMax: 8,
    band: "middle",
    uuid: "a1000001-0000-4000-8000-000000000008",
    subject: "Algebra",
    name: "Solve x + 5 = 12",
    difficulty: "intro",
    tags: ["linear", "one-step"],
    expectedAnswer: "x = 7",
    acceptedAnswers: ["x=7", "7"],
    prompt: "Solve x + 5 = 12\nSolve for x:\nx+5=12",
    hints: [
      "Undo the addition so x is alone.",
      "Subtract the same number from both sides.",
    ],
    explanation: "Subtract 5 from both sides: x = 12 − 5 = 7.",
    notations: [
      text(2, 28, 1, 2, "Solve for x:"),
      sym(2, 5, "x"),
      sym(3, 5, "+"),
      sym(4, 5, "5"),
      sym(5, 5, "="),
      sym(6, 5, "1"),
      sym(7, 5, "2"),
    ],
  },
  {
    id: "algebra-subtract-one-step",
    skillId: "algebra.linear-one-step",
    gradeMin: 7,
    gradeMax: 8,
    band: "middle",
    uuid: "a1000001-0000-4000-8000-000000000009",
    subject: "Algebra",
    name: "Solve x − 3 = 8",
    difficulty: "intro",
    tags: ["linear", "one-step"],
    expectedAnswer: "x = 11",
    acceptedAnswers: ["x=11", "11"],
    prompt: "Solve x − 3 = 8\nSolve for x:\nx-3=8",
    hints: [
      "Undo the subtraction so x is alone.",
      "Add the same number to both sides.",
    ],
    explanation: "Add 3 to both sides: x = 8 + 3 = 11.",
    notations: [
      text(2, 28, 1, 2, "Solve for x:"),
      sym(2, 5, "x"),
      sym(3, 5, "−"),
      sym(4, 5, "3"),
      sym(5, 5, "="),
      sym(6, 5, "8"),
    ],
  },
  {
    id: "algebra-multiply-one-step",
    skillId: "algebra.linear-one-step",
    gradeMin: 7,
    gradeMax: 8,
    band: "middle",
    uuid: "a1000001-0000-4000-8000-000000000010",
    subject: "Algebra",
    name: "Solve 4x = 20",
    difficulty: "intro",
    tags: ["linear", "one-step"],
    expectedAnswer: "x = 5",
    acceptedAnswers: ["x=5", "5"],
    prompt: "Solve 4x = 20\nSolve for x:\n4x=20",
    hints: [
      "x is multiplied by 4.",
      "Divide both sides by that coefficient.",
    ],
    explanation: "Divide both sides by 4: x = 20 / 4 = 5.",
    notations: [
      text(2, 28, 1, 2, "Solve for x:"),
      sym(2, 5, "4"),
      sym(3, 5, "x"),
      sym(4, 5, "="),
      sym(5, 5, "2"),
      sym(6, 5, "0"),
    ],
  },
  {
    id: "algebra-divide-one-step",
    skillId: "algebra.linear-one-step",
    gradeMin: 7,
    gradeMax: 8,
    band: "middle",
    uuid: "a1000001-0000-4000-8000-000000000011",
    subject: "Algebra",
    name: "Solve x / 3 = 4",
    difficulty: "core",
    tags: ["linear", "one-step"],
    expectedAnswer: "x = 12",
    acceptedAnswers: ["x=12", "12"],
    prompt: "Solve x / 3 = 4\nSolve for x:\nx/3=4",
    hints: [
      "x is divided by 3.",
      "Multiply both sides by that divisor.",
    ],
    explanation: "Multiply both sides by 3: x = 4 × 3 = 12.",
    notations: [
      text(2, 28, 1, 2, "Solve for x:"),
      sym(2, 5, "x"),
      sym(3, 5, "/"),
      sym(4, 5, "3"),
      sym(5, 5, "="),
      sym(6, 5, "4"),
    ],
  },
  {
    id: "algebra-square-equals",
    skillId: "algebra.square-root",
    gradeMin: 10,
    gradeMax: 11,
    band: "high",
    uuid: "a1000001-0000-4000-8000-000000000012",
    subject: "Algebra",
    name: "Solve x² = 49",
    difficulty: "core",
    tags: ["quadratic", "square-root"],
    expectedAnswer: "x = ±7",
    acceptedAnswers: [
      "±7",
      "x=±7",
      "x = 7 or x = -7",
      "x=7 or x=-7",
      "7 and -7",
    ],
    prompt: "Solve x² = 49\nSolve for x:\nx^2=49",
    hints: [
      "Take a square root of both sides.",
      "A positive number has two square roots.",
    ],
    explanation: "√49 = 7, and both 7 and −7 square to 49, so x = ±7.",
    notations: [
      text(2, 28, 1, 2, "Solve for x:"),
      sym(2, 5, "x"),
      exp(3, 5, "2"),
      sym(4, 5, "="),
      sym(5, 5, "4"),
      sym(6, 5, "9"),
    ],
  },
  {
    id: "algebra-slope-intercept",
    skillId: "algebra.slope-intercept",
    gradeMin: 8,
    gradeMax: 9,
    band: "middle",
    uuid: "a1000001-0000-4000-8000-000000000013",
    subject: "Algebra",
    name: "Line with slope 3 and intercept 2",
    difficulty: "intro",
    tags: ["linear", "slope", "intercept"],
    expectedAnswer: "y = 3x + 2",
    acceptedAnswers: ["y=3x+2", "y = 3x+2", "3x+2"],
    prompt:
      "Line with slope 3 and intercept 2\nWrite the equation of the line:\nslope 3, y-intercept 2",
    hints: [
      "Slope-intercept form is y = mx + b.",
      "m is the slope and b is the y-intercept.",
    ],
    explanation: "Substitute m = 3 and b = 2 into y = mx + b.",
    notations: [
      text(2, 28, 1, 2, "Write the equation of the line."),
      text(2, 22, 4, 5, "slope 3,  y-intercept 2"),
    ],
  },
  {
    id: "geometry-rectangle-area",
    skillId: "geometry.rectangle-area",
    gradeMin: 4,
    gradeMax: 5,
    band: "elementary",
    uuid: "a1000002-0000-4000-8000-000000000007",
    subject: "Geometry",
    name: "Area of a rectangle",
    difficulty: "intro",
    tags: ["area", "rectangle"],
    expectedAnswer: "20",
    acceptedAnswers: ["5×4=20", "5*4=20", "area = 20"],
    prompt: "Area of a rectangle\nFind the area of the rectangle.\n5 by 4",
    hints: [
      "The area of a rectangle is length times width.",
      "Multiply the two labeled side lengths.",
    ],
    explanation: "Area = 5 × 4 = 20.",
    notations: [
      text(2, 28, 1, 2, "Find the area."),
      line(8, 14, 28, 14),
      line(28, 14, 28, 4),
      line(28, 4, 8, 4),
      line(8, 4, 8, 14),
      ann(16, 15, "5"),
      ann(29, 9, "4"),
    ],
  },
  {
    id: "geometry-rectangle-perimeter",
    skillId: "geometry.rectangle-perimeter",
    gradeMin: 4,
    gradeMax: 5,
    band: "elementary",
    uuid: "a1000002-0000-4000-8000-000000000008",
    subject: "Geometry",
    name: "Perimeter of a rectangle",
    difficulty: "intro",
    tags: ["perimeter", "rectangle"],
    expectedAnswer: "18",
    acceptedAnswers: ["2(6+3)=18", "6+3+6+3=18", "perimeter = 18"],
    prompt:
      "Perimeter of a rectangle\nFind the perimeter of the rectangle.\n6 by 3",
    hints: [
      "The perimeter is the distance around the outside.",
      "Add all four sides. Opposite sides are equal.",
    ],
    explanation: "Perimeter = 2 × (6 + 3) = 18.",
    notations: [
      text(2, 28, 1, 2, "Find the perimeter."),
      line(8, 14, 26, 14),
      line(26, 14, 26, 5),
      line(26, 5, 8, 5),
      line(8, 5, 8, 14),
      ann(15, 15, "6"),
      ann(27, 9, "3"),
    ],
  },
  {
    id: "geometry-box-volume",
    skillId: "geometry.box-volume",
    gradeMin: 7,
    gradeMax: 8,
    band: "middle",
    uuid: "a1000002-0000-4000-8000-000000000009",
    subject: "Geometry",
    name: "Volume of a box",
    difficulty: "intro",
    tags: ["volume", "rectangle"],
    expectedAnswer: "24",
    acceptedAnswers: ["2×3×4=24", "2*3*4=24", "volume = 24"],
    prompt:
      "Volume of a box\nFind the volume of the box.\nlength 2, width 3, height 4",
    hints: [
      "The volume of a box is length × width × height.",
      "Multiply the three edge lengths.",
    ],
    explanation: "Volume = 2 × 3 × 4 = 24.",
    notations: [
      text(2, 28, 1, 2, "Find the volume of the box."),
      text(2, 22, 4, 5, "2 × 3 × 4"),
    ],
  },
  {
    id: "geometry-distance-3-4",
    skillId: "geometry.distance",
    gradeMin: 10,
    gradeMax: 11,
    band: "high",
    uuid: "a1000002-0000-4000-8000-000000000010",
    subject: "Geometry",
    name: "Distance between (0, 0) and (3, 4)",
    difficulty: "core",
    tags: ["distance", "pythagorean"],
    expectedAnswer: "5",
    acceptedAnswers: ["√25", "distance = 5"],
    prompt:
      "Distance between (0, 0) and (3, 4)\nFind the distance between the points.\n(0, 0) and (3, 4)",
    hints: [
      "The distance formula is √((x₂ − x₁)² + (y₂ − y₁)²).",
      "The horizontal and vertical changes form a right triangle.",
    ],
    explanation: "√(3² + 4²) = √(9 + 16) = √25 = 5.",
    notations: [
      text(2, 28, 1, 2, "Find the distance."),
      line(10, 16, 22, 6),
      ann(6, 17, "(0, 0)"),
      ann(23, 5, "(3, 4)"),
    ],
  },
  {
    id: "geometry-trapezoid-area",
    skillId: "geometry.trapezoid-area",
    gradeMin: 10,
    gradeMax: 11,
    band: "high",
    uuid: "a1000002-0000-4000-8000-000000000011",
    subject: "Geometry",
    name: "Area of a trapezoid",
    difficulty: "core",
    tags: ["area", "trapezoid"],
    expectedAnswer: "32",
    acceptedAnswers: ["(6+10)/2*4=32", "area = 32"],
    prompt:
      "Area of a trapezoid\nFind the area of the trapezoid.\nbases 6 and 10, height 4",
    hints: [
      "Area of a trapezoid is the average of the two bases times the height.",
      "Average the bases first, then multiply by the height.",
    ],
    explanation: "Area = (6 + 10) / 2 × 4 = 8 × 4 = 32.",
    notations: [
      text(2, 28, 1, 2, "Find the area."),
      line(12, 14, 28, 14),
      line(16, 5, 24, 5),
      line(12, 14, 16, 5),
      line(28, 14, 24, 5),
      ann(18, 15, "10"),
      ann(19, 4, "6"),
      ann(29, 9, "4"),
    ],
  },
  {
    id: "geometry-midpoint",
    skillId: "geometry.midpoint",
    gradeMin: 10,
    gradeMax: 11,
    band: "high",
    uuid: "a1000002-0000-4000-8000-000000000012",
    subject: "Geometry",
    name: "Midpoint of (2, 4) and (6, 8)",
    difficulty: "intro",
    tags: ["midpoint", "distance"],
    expectedAnswer: "(4, 6)",
    acceptedAnswers: ["(4,6)", "4, 6", "x = 4, y = 6"],
    prompt:
      "Midpoint of (2, 4) and (6, 8)\nFind the midpoint.\n(2, 4) and (6, 8)",
    hints: [
      "The midpoint averages the x-coordinates and the y-coordinates.",
      "Average each coordinate separately.",
    ],
    explanation: "Midpoint = ((2 + 6) / 2, (4 + 8) / 2) = (4, 6).",
    notations: [
      text(2, 28, 1, 2, "Find the midpoint."),
      line(10, 16, 26, 6),
      ann(6, 17, "(2, 4)"),
      ann(27, 5, "(6, 8)"),
    ],
  },
  {
    id: "general-unit-price",
    skillId: "arithmetic.unit-price",
    gradeMin: 6,
    gradeMax: 6,
    band: "elementary",
    uuid: "a1000006-0000-4000-8000-000000000007",
    subject: "General",
    name: "Price of one item",
    difficulty: "intro",
    tags: ["arithmetic", "unit-price"],
    expectedAnswer: "3",
    acceptedAnswers: ["$3", "3 dollars"],
    prompt:
      "Price of one item\nFour identical items cost 12.\nFind the price of one item.",
    hints: [
      "Divide the total cost by the number of items.",
      "Each item costs the same amount.",
    ],
    explanation: "12 / 4 = 3.",
    notations: [
      text(2, 28, 1, 2, "Four identical items cost 12."),
      text(2, 22, 4, 5, "Find the price of one."),
    ],
  },
  {
    id: "general-percent-off",
    skillId: "arithmetic.percent-off",
    gradeMin: 7,
    gradeMax: 8,
    band: "middle",
    uuid: "a1000006-0000-4000-8000-000000000008",
    subject: "General",
    name: "20% off a price of 50",
    difficulty: "core",
    tags: ["percent", "discount"],
    expectedAnswer: "40",
    acceptedAnswers: ["50-10=40", "sale price = 40"],
    prompt:
      "20% off a price of 50\nA price of 50 is reduced by 20%.\nFind the sale price.",
    hints: [
      "First find 20% of the original price.",
      "Subtract that discount from the original price.",
    ],
    explanation: "20% of 50 is 10, so the sale price is 50 − 10 = 40.",
    notations: [
      text(2, 28, 1, 2, "A price of 50 is reduced by 20%."),
      text(2, 22, 4, 5, "Find the sale price."),
    ],
  },
  {
    id: "statistics-two-heads",
    skillId: "stats.independent-events",
    gradeMin: 7,
    gradeMax: 8,
    band: "middle",
    uuid: "a1000005-0000-4000-8000-000000000007",
    subject: "Statistics",
    name: "Two heads in a row",
    difficulty: "intro",
    tags: ["probability", "independent"],
    expectedAnswer: "1/4",
    acceptedAnswers: ["0.25", "1/4", "0.25"],
    prompt:
      "Two heads in a row\nA fair coin is flipped twice.\nFind the probability of two heads.",
    hints: [
      "Each flip is independent, so multiply the two probabilities.",
      "The probability of heads on one flip is 1/2.",
    ],
    explanation: "P(HH) = (1/2) × (1/2) = 1/4.",
    notations: [
      text(2, 28, 1, 2, "A fair coin is flipped twice."),
      text(2, 22, 4, 5, "Find P(two heads)."),
    ],
  },
  {
    id: "statistics-complement",
    skillId: "stats.complement",
    gradeMin: 8,
    gradeMax: 9,
    band: "middle",
    uuid: "a1000005-0000-4000-8000-000000000008",
    subject: "Statistics",
    name: "Probability of the complement",
    difficulty: "core",
    tags: ["probability", "complement"],
    expectedAnswer: "0.7",
    acceptedAnswers: ["7/10", "0.70", "70%"],
    prompt:
      "Probability of the complement\nThe probability of rain is 0.3.\nFind the probability that it does not rain.",
    hints: [
      "An event and its complement add up to 1.",
      "Subtract the given probability from 1.",
    ],
    explanation: "P(no rain) = 1 − 0.3 = 0.7.",
    notations: [
      text(2, 28, 1, 2, "P(rain) = 0.3"),
      text(2, 22, 4, 5, "Find P(no rain)."),
    ],
  },
  {
    id: "statistics-marbles",
    skillId: "stats.sample-space",
    gradeMin: 7,
    gradeMax: 8,
    band: "middle",
    uuid: "a1000005-0000-4000-8000-000000000009",
    subject: "Statistics",
    name: "Drawing a red marble",
    difficulty: "intro",
    tags: ["probability", "sample-space"],
    expectedAnswer: "3/10",
    acceptedAnswers: ["0.3", "3/10"],
    prompt:
      "Drawing a red marble\nA bag has 3 red marbles and 7 blue marbles.\nFind the probability of drawing a red marble.",
    hints: [
      "Probability is the number of favorable outcomes over the total.",
      "Count the red marbles and the marbles in the whole bag.",
    ],
    explanation: "There are 10 marbles, and 3 are red, so the probability is 3/10.",
    notations: [
      text(2, 28, 1, 2, "3 red marbles and 7 blue marbles."),
      text(2, 22, 4, 5, "Find P(red)."),
    ],
  },
  {
    id: "statistics-mutually-exclusive",
    skillId: "stats.mutually-exclusive",
    gradeMin: 10,
    gradeMax: 11,
    band: "high",
    uuid: "a1000005-0000-4000-8000-000000000010",
    subject: "Statistics",
    name: "Probability of 1 or 2 on a die",
    difficulty: "intro",
    tags: ["probability", "sample-space"],
    expectedAnswer: "1/3",
    acceptedAnswers: ["2/6", "1/3"],
    prompt:
      "Probability of 1 or 2 on a die\nA fair die is rolled once.\nFind the probability of rolling a 1 or a 2.",
    hints: [
      "These two outcomes cannot happen together.",
      "Add their separate probabilities.",
    ],
    explanation: "P(1) + P(2) = 1/6 + 1/6 = 1/3.",
    notations: [
      text(2, 28, 1, 2, "A fair die is rolled once."),
      text(2, 22, 4, 5, "Find P(1 or 2)."),
    ],
  },
  {
    id: "statistics-combinations",
    skillId: "stats.combinations",
    gradeMin: 11,
    gradeMax: 12,
    band: "high",
    uuid: "a1000005-0000-4000-8000-000000000011",
    subject: "Statistics",
    name: "Ways to choose 2 of 5",
    difficulty: "core",
    tags: ["combinations", "probability"],
    expectedAnswer: "10",
    acceptedAnswers: ["C(5,2) = 10", "C(5,2)=10", "10 ways"],
    prompt:
      "Ways to choose 2 of 5\nHow many ways can you choose 2 items from 5?\nOrder does not matter.",
    hints: [
      "Use the combination C(n, k) = n! / (k!(n − k)!).",
      "Order does not matter, so this is not a permutation.",
    ],
    explanation: "C(5, 2) = (5 × 4) / (2 × 1) = 10.",
    notations: [
      text(2, 28, 1, 2, "Choose 2 items from 5."),
      text(2, 22, 4, 5, "Order does not matter."),
    ],
  },
  {
    id: "statistics-relative-frequency",
    skillId: "stats.relative-frequency",
    gradeMin: 10,
    gradeMax: 11,
    band: "high",
    uuid: "a1000005-0000-4000-8000-000000000012",
    subject: "Statistics",
    name: "Relative frequency",
    difficulty: "intro",
    tags: ["frequency", "probability"],
    expectedAnswer: "0.2",
    acceptedAnswers: ["1/5", "8/40", "20%"],
    prompt:
      "Relative frequency\nAn outcome occurred 8 times in 40 trials.\nFind the relative frequency.",
    hints: [
      "Relative frequency is the count divided by the number of trials.",
      "Write the result as a decimal or a simplified fraction.",
    ],
    explanation: "8 / 40 = 1/5 = 0.2.",
    notations: [
      text(2, 28, 1, 2, "8 occurrences in 40 trials."),
      text(2, 22, 4, 5, "Find the relative frequency."),
    ],
  },
  {
    id: "statistics-weighted-mean",
    skillId: "stats.weighted-mean",
    gradeMin: 10,
    gradeMax: 11,
    band: "high",
    uuid: "a1000005-0000-4000-8000-000000000013",
    subject: "Statistics",
    name: "Weighted mean of two scores",
    difficulty: "core",
    tags: ["mean", "weighted-mean"],
    expectedAnswer: "82",
    acceptedAnswers: ["82.0"],
    prompt:
      "Weighted mean of two scores\nTwo scores of 70 and three scores of 90.\nFind the mean.",
    hints: [
      "Multiply each score by how many times it appears.",
      "Divide the total by the number of scores.",
    ],
    explanation: "(2 × 70 + 3 × 90) / 5 = (140 + 270) / 5 = 82.",
    notations: [
      text(2, 28, 1, 2, "Scores: 70, 70, 90, 90, 90"),
      text(2, 22, 4, 5, "Find the mean."),
      ...dataDotPlotFigure([70, 70, 90, 90, 90]),
    ],
  },
];

export function getPracticeQuestionTemplate(
  id: string,
): PracticeQuestionTemplate | undefined {
  return PRACTICE_QUESTION_TEMPLATES.find((t) => t.id === id);
}

export function getPracticeQuestionTemplateByUUId(
  uuid: string,
): PracticeQuestionTemplate | undefined {
  return PRACTICE_QUESTION_TEMPLATES.find((t) => t.uuid === uuid);
}

export function practiceTemplateStemParts(
  template: PracticeQuestionTemplate,
): PracticeProblemPart[] | null {
  if (!template.parts || template.parts.length < 2) return null;
  return template.parts.map((part) => ({
    id: String(part.id),
    text: part.text,
  }));
}

export function practiceTemplateAnswers(
  template: PracticeQuestionTemplate,
  activePartId?: string | null,
): { expectedAnswer: string; acceptedAnswers?: string[] } {
  const id = (activePartId ?? "").trim();
  if (id && template.parts && template.parts.length >= 2) {
    const part = template.parts.find((p) => String(p.id) === id);
    if (part) {
      return {
        expectedAnswer: part.expectedAnswer,
        acceptedAnswers: part.acceptedAnswers,
      };
    }
  }
  return {
    expectedAnswer: template.expectedAnswer,
    acceptedAnswers: template.acceptedAnswers,
  };
}

/** Hints for the active task. Multi-part worksheets do not reuse another task's tips. */
export function practiceHintsForPart(
  template: PracticeQuestionTemplate | undefined,
  activePartId?: string | null,
): string[] {
  if (!template) return [];
  const parts = template.parts;
  if (parts && parts.length >= 2) {
    const id = (activePartId ?? "").trim();
    const part = id
      ? parts.find((p) => String(p.id) === id)
      : undefined;
    return part?.hints?.length ? part.hints : [];
  }
  return template.hints ?? [];
}

export function nextHintsRevealed(
  hintCount: number,
  revealedCount: number,
): number {
  if (hintCount <= 0) return 0;
  return Math.min(Math.max(0, revealedCount) + 1, hintCount);
}

export function revealedPracticeHint(
  hints: string[] | undefined,
  revealedCount: number,
): string | null {
  if (!hints?.length || revealedCount <= 0) return null;
  return hints[Math.min(revealedCount, hints.length) - 1] ?? null;
}

function isDiagramPointLabel(value: string): boolean {
  return /^[A-Za-z]{1,3}$/.test(value.trim());
}

/** Figure overlay: lines, curves, circles, annotations, and vertex letters. */
export function isPracticeDiagramStemNotation(item: {
  kind?: string;
  notationType?: string;
  value?: string;
}): boolean {
  const kind = item.kind ?? item.notationType ?? "";
  if (DIAGRAM_STEM_KINDS.has(kind)) return true;
  if (kind === "SYMBOL" && isDiagramPointLabel(String(item.value ?? ""))) {
    return true;
  }
  return false;
}

const FIGURE_SHAPE_KINDS = new Set(["LINE", "CURVE", "CIRCLE", "CONIC"]);

type DiagramStemLike = { kind?: string; notationType?: string; value?: string };

/**
 * The stems that form a question's figure. Empty when nothing is drawn, so a
 * lone equation letter such as the x in 2x+3=11 is never mistaken for a vertex.
 */
export function practiceDiagramStems<T extends DiagramStemLike>(items: T[]): T[] {
  const hasShape = items.some((item) =>
    FIGURE_SHAPE_KINDS.has(item.kind ?? item.notationType ?? ""),
  );
  if (!hasShape) return [];
  return items.filter((item) => isPracticeDiagramStemNotation(item));
}

/**
 * One-line description of the figure already drawn for a question, for the
 * tutor prompt (e.g. "3 lines; labels: 50°, 60°, ?"). Null when there is none.
 */
export function describePracticeDiagramStem(
  template: PracticeQuestionTemplate,
): string | null {
  const stems = practiceDiagramStems(template.notations);
  if (stems.length === 0) return null;

  const shapeNames: Record<string, string> = {
    LINE: "line",
    CURVE: "curve",
    CIRCLE: "circle",
    CONIC: "conic",
  };
  const counts = new Map<string, number>();
  const labels: string[] = [];
  const vertices: string[] = [];
  for (const item of stems) {
    const name = shapeNames[item.kind];
    if (name) {
      counts.set(name, (counts.get(name) ?? 0) + 1);
    } else if (item.kind === "ANNOTATION") {
      labels.push(item.value.trim());
    } else if (item.kind === "SYMBOL") {
      vertices.push(item.value.trim());
    }
  }

  const parts: string[] = [];
  for (const [name, count] of counts) {
    parts.push(count === 1 ? `1 ${name}` : `${count} ${name}s`);
  }
  if (vertices.length) parts.push(`vertices ${vertices.join(", ")}`);
  if (labels.length) parts.push(`labels: ${labels.join(", ")}`);
  return parts.join("; ");
}

function isMathSymbolRow(value: string): boolean {
  const t = value.trim();
  if (!t || isDiagramPointLabel(t)) return false;
  return /[\d=+\-×÷*/^()∫]/.test(t);
}

/** Flatten stem text into a problem statement: title, question, givens. */
export function formatPracticeProblemPrompt(
  template: PracticeQuestionTemplate,
  locale: PracticeLocale = "en",
): string {
  const authored = template.prompt?.trim();
  if (authored) return authored;

  const seen = new Set<string>();
  const take = (bucket: string[], raw: string) => {
    const line = raw.trim();
    if (!line) return;
    const key = line.replace(/\s+/g, "");
    if (seen.has(key)) return;
    seen.add(key);
    bucket.push(line);
  };

  const title: string[] = [];
  const questions: string[] = [];
  const givens: string[] = [];
  const expressions: string[] = [];
  take(title, template.name);

  const symbolsByRow = new Map<number, Array<{ col: number; text: string }>>();
  for (const item of template.notations) {
    switch (item.kind) {
      case "TEXT":
        take(questions, item.value);
        break;
      case "ANNOTATION":
        if (!isDiagramPointLabel(item.value)) take(givens, item.value);
        break;
      case "SYMBOL":
      case "EXPONENT": {
        const text =
          item.kind === "EXPONENT" ? "^" + item.value : item.value;
        const list = symbolsByRow.get(item.row) ?? [];
        list.push({ col: item.col, text });
        symbolsByRow.set(item.row, list);
        break;
      }
      default:
        break;
    }
  }

  const rows = [...symbolsByRow.keys()].sort((a, b) => a - b);
  for (const row of rows) {
    const cells = (symbolsByRow.get(row) ?? []).sort((a, b) => a.col - b.col);
    const joined = cells.map((c) => c.text).join("");
    if (isMathSymbolRow(joined)) take(expressions, joined);
  }

  const lines = [...title, ...questions, ...expressions];
  if (givens.length) {
    if (questions.length || expressions.length) {
      lines.push(locale === "he" ? "נתון:" : "Given:");
    }
    lines.push(...givens);
  }
  return lines.join("\n");
}
